import { useEffect, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ResultReason } from 'microsoft-cognitiveservices-speech-sdk';

import ConvContext from '../../../store/conversation-context';
import UserContext from '../../../store/user-context';
import { LANGUAGES } from '../../../App';
import useFetch from '../../../hooks/use-fetch';
import { useSpeechSDK } from '../../Helpers/Conversation/SpeechDetection';
import useAnalyticsEventTracker from '../../../hooks/use-analytics';

let recognizer = null,
  recognition = null;

const SpeechRecognition = () => {
  const { apiUrl, speechRegion, speechKey } = window['appConfig'];
  const dispatch = useDispatch();
  const lesson = useSelector((state) => state.currentLesson);
  // const microphoneActive = useSelector((state) => state.microphoneActive);
  const recognisingSpeech = useSelector((state) => state.recognising);
  const reference = useRef();
  reference.current = recognisingSpeech;
  const activeButton = useSelector((state) => state.activeButton);
  const activeSection = useSelector((state) => state.activeSection);
  const sceneID = useSelector((state) => state.currentSceneID);
  const { convContext, setConvContext } = useContext(ConvContext);
  const userContext = useContext(UserContext);
  const learningLang = userContext.user.user.learning;
  const speechsdk = require('microsoft-cognitiveservices-speech-sdk');
  const {
    data: speechData,
    loading: speechLoading,
    error: speechError,
    initFunc: callFetch,
  } = useFetch();
  const gaEventTracker = useAnalyticsEventTracker('Mic Errors');

  let activeLangCode = Array.from(LANGUAGES.values())
    .map((lang) => {
      return lang.name === learningLang && lang.code;
    })
    .filter((e) => e !== false)
    .toString();

  /**
   * Helper to tidy up end of recognition and show something to the user
   *
   * @param {string} text - Text to be displayed to user
   * @param {int} score - Score from recognition for frontend to do something with
   * @param {boolean} cancellation - used to set values even if reference is false
   */
  const endRecognition = async (
    text = null,
    score = null,
    cancellation = false,
  ) => {
    //using a reference to ensure this function uses correct value of recognisingSpeech during async calls
    if (reference.current === true || cancellation === true) {
      if (text !== null) {
        dispatch({
          type: 'setRecognitionText',
          payload: { text: text },
        });
      }

      if (score !== null) {
        dispatch({
          type: 'setCurrentButtonScore',
          payload: { score: score },
        });

        dispatch({
          type: 'deactivateMicrophone',
        });

        dispatch({
          type: 'stopSpeechRecognition',
        });
      }
    }

    dispatch({
      type: 'deactivateMicrophone',
    });
  };

  const chromeSpeechRecog = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    recognition = new SpeechRecognition();

    // let conversations = convContext.conversations;
    // let parts = conversations[convContext.conversationId].parts //TODO: make dynamic for section of conversation we're on
    // let grammar = '#JSGF V1.0; grammar parts; public parts = ' + Array.from(parts.values()).map(e => e.learningLang).join(" | ") + ' ;'
    // let speechRecognitionList = new SpeechGrammarList();
    // speechRecognitionList.addFromString(grammar, 1);
    // recognition.grammars = speechRecognitionList;
    recognition.continuous = true;
    recognition.lang = activeLangCode;
    recognition.interimResults = false;
    recognition.maxAlternatives = 5;
    let timer = '';
    let textResult = [];

    recognition.onstart = () => {
      dispatch({
        type: 'initMicrophone',
      });
    };

    recognition.onend = () => {
      // console.log('ended')
      recognition.stop();
    };

    recognition.onnomatch = () => {
      endRecognition('NOMATCH: Speech could not be recognized.', 0);
    };

    recognition.onspeechend = () => {
      // console.log("onspeechend")
      if (textResult.length === 0) {
        timer = setTimeout(function () {
          console.log('cancel');
          endRecognition(
            'NOMATCH: No audio detected. Please try again.',
            0,
            true,
          );
        }, 500);
      }
      recognition.stop();
    };

    recognition.onerror = (event) => {
      gaEventTracker('Chrome Recognition Error', `Error: ${event.message}`)

      console.warn(event);
      endRecognition(
        'MIC ERROR',
      );
    };

    recognition.onresult = (event) => {
      // console.log('result')

      for (let i = 0; i < event.results[event.results.length - 1].length; i++) {
        let s = event.results[event.results.length - 1][i].transcript;
        textResult.push(s);
      }

      recognition.stop();
      clearTimeout(timer); //Stop timer as we have results!
      evaluateInput(textResult);
      endRecognition();
    };

    recognition.start();
  };

  const renewToken = async () => {
    const tokenUrl = `https://${speechRegion}.api.cognitive.microsoft.com/sts/v1.0/issueToken`;
    const fetchOptions = {
      method: 'POST',
      headers: {
        'Ocp-Apim-Subscription-Key': speechKey,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };

    const response = await fetch(tokenUrl, fetchOptions);
    const token = await response.text();

    return token;
  }

  const iosSpeechRecog = async () => {
    let timer = '';
    const sentences = convContext.sentences
    let token = convContext.microsoftToken;
    const expiry = (JSON.parse(window.atob(token.split('.')[1]))).exp;
    const fiveMinsTime = (Math.floor(((new Date).getTime() + 300000) / 1000)); 

    if( ! sentences ) {
      cancelSDK();
      return;
    }

    const currentSectionSplits = sentences[activeSection]?.splits;

    //Get new token if old one is going to expire in 10 mins or less
    if( fiveMinsTime >= expiry ) {
      token = await renewToken();
      setConvContext((prevState) => ({
        ...prevState,
        microsoftToken: token,
      }));
    }

    let speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(
      token,
      speechRegion,
    );
    speechConfig.speechRecognitionLanguage = activeLangCode;
    
    const audioConfig = speechsdk.AudioConfig.fromDefaultMicrophoneInput();
        
    recognizer = new speechsdk.SpeechRecognizer(speechConfig, audioConfig);

    const phraseList = speechsdk.PhraseListGrammar.fromRecognizer(recognizer);
    phraseList.addPhrase(userContext.user.user.firstname);
    phraseList.addPhrase(userContext.user.user.lastname);
    if( currentSectionSplits ) {
      currentSectionSplits.forEach(split => {
        phraseList.addPhrase(split.learningLang);
      });
    }

    if (recognizer !== null) {
      timer = setTimeout(function () {
        gaEventTracker('SDK Recognition Timeout')
        endRecognition(
          'ERROR: Recognition Failed to Start. Please try again.',
          0,
        );
      }, 5000);
    }

    recognizer.sessionStarted = () => {
      clearTimeout(timer);
      dispatch({
        type: 'initMicrophone',
      });
    };

    recognizer.recognizeOnceAsync(
      (result) => {
        let recorderInstructions,
          score = null;
        clearTimeout(timer);

        // console.log(ResultReason)

        switch (result.reason) {
          case ResultReason.RecognizedSpeech: {
            // recorderInstructions = `RECOGNIZED: ${result.text}`;
            recorderInstructions = null;
            // recorderState = ('finished');
            evaluateInput(result.text);
            break;
          }
          case ResultReason.NoMatch: {
            recorderInstructions = 'NOMATCH: Speech could not be recognized.';
            score = 0;
            break;
          }
          case ResultReason.Canceled: {
            console.log(result);
            const cancellation = result.privErrorDetails;
            recorderInstructions = `CANCELED: Reason=${cancellation}`;
            gaEventTracker('SDK Recognition Error', `Cancelled: ${cancellation}`);

            // if (cancellation.reason == speechsdk.CancellationReason.Error) {
            //   recorderInstructions = `CANCELED: ErrorCode=${cancellation.ErrorCode}. ErrorDetails=${cancellation.errorDetails}. Did you update the key and location/region info?`;
            // }

            score = 0;
            break;
          }
        }

        endRecognition(recorderInstructions, score);
      },
      (error) => {
        console.warn(error);
        clearTimeout(timer);
        gaEventTracker('SDK Recognition Error', `Error: ${error}`)
        endRecognition(
          'MIC ERROR',
        );
      },
    );
  };

  const initSDK = () => {
    if (useSpeechSDK()) {
      iosSpeechRecog();
    } else {
      chromeSpeechRecog();
    }
  };

  const cancelSDK = () => {
    if (useSpeechSDK()) {
      if (recognizer) {
        recognizer.stopContinuousRecognitionAsync(
          () => {
           recognizer.close(); 
            recognizer = null;
          },
          (err) => {
            console.warn(err);
          },
        );
      }
    } else {
      if (recognition) {
        recognition.stop();
      }
    }
  };

  const evaluateInput = (inputValue) => {
    let formData = null;
    let fetchOptions = null;
    const conversationId = parseInt(activeSection) + 1;
    let textUrl = `${apiUrl}/conversation/spoken/${sceneID}/${conversationId}/${activeButton}?lesson=${lesson}`;

    //Url is different for full conversation
    if (lesson > 1) {
      textUrl = `${apiUrl}/conversation/spoken/${sceneID}/${conversationId}?lesson=${lesson}`;
    }

    formData = new URLSearchParams();

    fetchOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    };

    //TODO: fix this on server side
    if (useSpeechSDK()) {
      formData.append('typed', encodeURIComponent(inputValue));
    } else {
      formData.append('typed', inputValue);
    }

    callFetch({ url: textUrl, options: fetchOptions });
  };

  useEffect(() => {
    // console.log(`recognisingSpeech: ${recognisingSpeech}`);
    if (recognisingSpeech === true) {
      initSDK();
    } else {
      cancelSDK();
    }
  }, [recognisingSpeech]);

  useEffect(() => {
    if (speechData) {
      dispatch({
        type: 'setRecognitionText',
        payload: { text: `RECOGNISED: ${speechData.recognised}` },
      });

      setTimeout(function () {
        dispatch({
          type: 'setCurrentButtonScore',
          payload: { score: speechData.score },
        });

        dispatch({
          type: 'deactivateMicrophone',
        });

        dispatch({
          type: 'stopSpeechRecognition',
        });
      }, 500);
    }
  }, [speechData]);

  useEffect(() => {
    if (speechError) {
      dispatch({
        type: 'setRecognitionText',
        payload: { text: `Server Error: ${speechError}` },
      });

      setTimeout(function () {
        // dispatch({
        //   type: 'setCurrentButtonScore',
        //   payload: { score: speechData.score },
        // });

        dispatch({
          type: 'deactivateMicrophone',
        });

        dispatch({
          type: 'stopSpeechRecognition',
        });
      }, 500);
    }
  }, [speechError]);

  useEffect(() => {
    if (speechLoading) {
      dispatch({
        type: 'setRecognitionText',
        payload: { text: `Attempting recognition...` },
      });
    }
  }, [speechLoading]);

  return null;
};

export default SpeechRecognition;
